import { isValidHttpUrl } from "replo-utils/lib/url";
import { z } from "zod";

import { productEmailFormBaseSchema, productEmailSchema } from "./productEmail";
import { productFileSchema } from "./productFile";
import { coerceStringToBooleanSchema } from "./utils";

export interface StoreProductSummary {
  title: string;
  id: number;
  featuredImage: string | null;
  defaultVariantId: number;
}

/**
 * Note (Noah, 2023-03-12): This sentinel value is used to indicate that the
 * user has specifically selected one-time purchase. If that's the case, then
 * the dynamic data value for selected selling plan id will always resolve to
 * null. If the selectedSellingPlanId is not set and an action references it
 * with dynamic data, it will resolved to the first selling plan (instead of no
 * selling plan).
 *
 * This is because we need to support the case where you have two
 * tabs, one with OPT and one with SNS, where the SNS tab has a selling plan
 * dropdown - we want that selling plan dropdown to default to the first selling
 * plan, and for the buttons inside that tab to resolve the first selling plan
 * with dynamic data. However, the other case we want to support is just having
 * one selling plan dropdown, not inside tabs - in this case, the selected selling
 * plan dynamic data value needs to resolve to null if the user specifically selects
 * OTP from the dropdown.
 *
 * Note: we could do this with null vs undefined, but a sentinel value is harder
 * to screw up.
 */
export type SelectedSellingPlanIdOrOneTimePurchase =
  | number
  | { __reploOneTimePurchase: true };

const metafieldTypeSchema = z.enum([
  "boolean",
  "collection_reference",
  "color",
  "customer_reference",
  "date_time",
  "date",
  "dimension",
  "file_reference",
  "id",
  "json",
  "link",
  "list.collection_reference",
  "list.color",
  "list.customer_reference",
  "list.date_time",
  "list.date",
  "list.dimension",
  "list.file_reference",
  "list.id",
  "list.link",
  "list.metaobject_reference",
  "list.mixed_reference",
  "list.number_decimal",
  "list.number_integer",
  "list.page_reference",
  "list.product_reference",
  "list.product_taxonomy_value_reference",
  "list.rating",
  "list.single_line_text_field",
  "list.url",
  "list.variant_reference",
  "list.volume",
  "list.weight",
  "metaobject_reference",
  "mixed_reference",
  "money",
  "multi_line_text_field",
  "number_decimal",
  "number_integer",
  "page_reference",
  "product_reference",
  "product_taxonomy_value_reference",
  "rating",
  "rich_text_field",
  "single_line_text_field",
  "url",
  "variant_reference",
  "volume",
  "weight",
]);

export interface ProductRef {
  productId: number | string;
  variantId?: number;
  quantity?: number;
  title?: string;
  id?: number;
}
export type ProductRefOrDynamic = ProductRef | ContextRef;

export interface ContextRef {
  type: "contextRef";
  ref: string;
}

export const metafieldDefinitionSchema = z
  .object({
    key: z.string(),
    name: z.string(),
    namespace: z.string(),
    id: z.string(),
    type: metafieldTypeSchema,
    description: z.string().nullable(),
    ownerType: z.enum(["PRODUCT", "PRODUCTVARIANT", "PAGE"]),
  })
  .describe("MetafieldDefinition");

const metafieldValuesMapping = z.record(
  z.string(),
  z.object({
    type: metafieldTypeSchema,
    value: z.union([z.number(), z.string()]),
  }),
);

export const productMetafieldMappingSchema = z
  .record(z.string(), z.record(z.string(), metafieldValuesMapping))
  .describe("ProductMetafieldMapping");

export const variantMetafieldMappingSchema = z
  .record(z.string(), z.record(z.string(), metafieldValuesMapping))
  .describe("VariantMetafieldMapping");

const productStatusSchema = z.string(); // Assuming ProductStatus is a string enum
const shopifySellingPlanGroupSchema = z.object({
  appId: z.string().nullable(),
  options: z.array(
    z.object({
      name: z.string(),
      position: z.number(),
      values: z.array(z.union([z.string(), z.null()])),
    }),
  ),
  sellingPlans: z.array(
    z.object({
      id: z.union([z.number(), z.string()]),
      name: z.string(),
      description: z.string().nullable(),
      options: z.array(
        z.object({
          name: z.string().optional(),
          position: z.number(),
          value: z.string(),
        }),
      ),
      priceAdjustments: z.array(
        z.object({
          value: z.number(),
          value_type: z.enum(["percentage", "fixed_amount", "price"]),
        }),
      ),
      appName: z.string().nullable().optional(),
    }),
  ),
});

const reploShopifyOptionSchema = z.object({
  key: z.enum(["option1", "option2", "option3"]),
  name: z.string(),
  values: z
    .array(
      z.object({
        title: z.string(),
        available: z.boolean().optional(),
      }),
    )
    .optional(),
});

const reploShopifyVariantSchema = z.object({
  id: z.number(),
  variantId: z.number(),
  productId: z.union([z.string(), z.number()]),
  sku: z.string().nullable(),
  name: z.string(),
  option1: z.string(),
  option2: z.string().nullable().optional(),
  option3: z.string().nullable().optional(),
  title: z.string(),
  available: z.boolean(),
  featuredImage: z.string().nullable(),
  productHandle: z.string(),
  variantMetafields: z
    .record(z.string(), z.record(z.string(), z.union([z.string(), z.number()])))
    .optional(),
  sellingPlanIds: z.array(z.number()),
});

export const reploShopifyProductSchema = z
  .object({
    id: z.number(),
    productId: z.union([z.string(), z.number()]),
    title: z.string(),
    type: z.string().optional(),
    images: z.array(z.string()),
    status: productStatusSchema,
    featured_image: z.string().nullable(),
    variants: z.array(reploShopifyVariantSchema),
    variant: reploShopifyVariantSchema,
    variantId: z.number(),
    options: z.array(reploShopifyOptionSchema),
    optionsValues: z.array(z.string()),
    description: z.string(),
    handle: z.string(),
    quantity: z.number(),
    productMetafields: z
      .record(
        z.string(),
        z.record(z.string(), z.union([z.string(), z.number()])),
      )
      .optional(),
    sellingPlanGroups: z.array(shopifySellingPlanGroupSchema),
  })
  .describe("SchemaReploShopifyProduct");

export const reploProductStatusSchema = z
  .enum(["active", "archived", "draft"])
  .describe("ReploProductStatus");
export const productReferenceTypeSchema = z
  .enum(["SHOPIFY", "REPLO"])
  .describe("ProductReferenceType");

const sellingPlanPriceAdjustmentSchema = z.object({
  value: z.number(),
  valueType: z.enum(["percentage", "fixedAmount", "price"]),
});

const sellingPlanOptionValueSchema = z.object({
  name: z.string().optional(),
  value: z.string(),
});

const sellingPlanSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  optionValues: z.array(sellingPlanOptionValueSchema),
  priceAdjustments: z.array(sellingPlanPriceAdjustmentSchema),
  appId: z.string().nullable().optional(),
  price: z.string().optional(),
  externalId: z.string().nullable(),
});

export const sellingPlanGroupSchema = z
  .object({
    appId: z.string().nullable(),
    options: z.array(
      z.object({
        name: z.string(),
        values: z.array(z.union([z.string(), z.null()])),
      }),
    ),
    sellingPlans: z.array(sellingPlanSchema),
  })
  .describe("SellingPlanGroup");

export const imageMediaSchema = z
  .object({
    id: z.uuid(),
    type: z.literal("image"),
    alt: z.string().nullable(),
    url: z.url(),
    width: z.number().nullable(),
    height: z.number().nullable(),
    sortOrder: z.number(),
  })
  .describe("ImageMedia");

const videoMediaSourceSchema = z.object({
  url: z.url(),
  mimeType: z.string(),
  format: z.string(),
  width: z.number().nullable(),
  height: z.number().nullable(),
});

const videoPreviewImageSchema = imageMediaSchema.pick({
  alt: true,
  url: true,
  width: true,
  height: true,
});

const videoMediaSchema = z.object({
  id: z.uuid(),
  type: z.literal("video"),
  alt: z.string().nullable(),
  sources: z.array(videoMediaSourceSchema),
  previewImage: videoPreviewImageSchema.nullable(),
  sortOrder: z.number(),
});

const externalVideoMediaSchema = z.object({
  id: z.uuid(),
  type: z.literal("externalVideo"),
  alt: z.string().nullable(),
  embedUrl: z.url(),
  host: z.string(),
  originUrl: z.url(),
  previewImage: videoPreviewImageSchema.nullable(),
  sortOrder: z.number(),
});

export const mediaItemSchema = z
  .discriminatedUnion("type", [
    imageMediaSchema,
    videoMediaSchema,
    externalVideoMediaSchema,
  ])
  .describe("MediaItem");

export const optionValueSchema = z
  .object({
    id: z.uuid(),
    value: z.string(),
    color: z.string().nullable(),
    image: z.url().nullable(),
    media: z.array(mediaItemSchema).optional(),
    referenceType: productReferenceTypeSchema,
    externalId: z.string().nullable(),
  })
  .describe("OptionValue");

export const optionSchema = z
  .object({
    id: z.uuid(),
    title: z.string(),
    optionValues: z.array(optionValueSchema),
    referenceType: productReferenceTypeSchema,
    externalId: z.string().nullable(),
  })
  .describe("Option");

export const variantSchema = z
  .object({
    id: z.uuid(),
    title: z.string().nullish(),
    sku: z.string().nullish(),
    price: z.number().nullish(),
    originalPrice: z.number().nullish(),
    priceCurrencyCode: z.string().nullish(),
    unitCost: z.number().nullish(),
    unitCostCurrencyCode: z.string().nullish(),
    optionValues: z.array(optionValueSchema),
    media: z.array(mediaItemSchema).nullish(),
    referenceType: productReferenceTypeSchema,
    stripePriceId: z.string().nullish(),
    featuredImage: z.object({ src: z.string() }).nullish(),
    externalId: z.string().nullish(),
  })
  .describe("Variant");

export const productSchema = z
  .object({
    id: z.uuid(),
    title: z.string(),
    description: z.string().nullish(),
    status: reploProductStatusSchema,
    slug: z.string().nullish(),
    referenceType: productReferenceTypeSchema,
    externalId: z.string().nullish(),
    stripeProductId: z.string().nullish(),
    vendor: z.string().nullish(),
    type: z.string().nullish(),
    ctaUrl: z.string().nullish(),
    media: z.array(mediaItemSchema).nullish(),
    featuredMedia: imageMediaSchema.nullish(),
    options: z.array(optionSchema),
    variants: z.array(variantSchema),
    sellingPlanGroups: z.array(sellingPlanGroupSchema).nullish(),
    externalCheckoutUrl: z.string().nullish(),
    productFiles: z.array(productFileSchema).nullish(),
    productEmails: z.array(productEmailSchema).nullish(),
  })
  .describe("Product");

export const productEmbeddingSchema = z
  .object({
    id: z.uuid(),
    title: z.string(),
    status: reploProductStatusSchema,
    description: z.string().nullable(),
    externalRawJson: z.any(),
    referenceType: productReferenceTypeSchema,
    embeddingHash: z.string().nullable(),
    embeddingVersion: z.string().nullable(),
    embeddingGeneratedAt: z.date().nullable(),
  })
  .describe("ProductEmbeddingData");

export const produceProductTextAndEmbeddingsBatchSchema = z
  .object({
    productIds: z.array(z.uuid()),
  })
  .describe("ProduceProductTextAndEmbeddingsBatch");

export const reploProductCreateOrUpdateOptionsSchema = productSchema
  .pick({
    id: true,
    title: true,
    description: true,
    type: true,
    externalCheckoutUrl: true,
  })
  .extend({
    price: z.number(),
    compareAtPrice: z.number().nullish(),
    media: z
      .array(
        z.object({
          assetId: z.uuid(),
          sortOrder: z.number(),
        }),
      )
      .optional(),
    productFiles: z
      .array(
        z.object({
          id: z.string(),
          url: z.string(),
          name: z.string(),
          sizeBytes: z.number(),
          fileExtension: z.string(),
          label: z.string().optional(),
        }),
      )
      .optional(),
    productEmail: productEmailFormBaseSchema.nullish(),
  })
  .describe("ReploProductCreateOrUpdateOptions");

// TODO (Noah, 2025-05-28): Remove after a week
export const productsQuerySchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("firstN"),
      projectId: z.uuid(),
      n: z.preprocess(Number, z.number()),
    }),
    z.object({
      type: z.literal("specificShopifyProductIds"),
      projectId: z.uuid(),
      pageSize: z.preprocess(Number, z.number()),
      nextPage: z.string().optional(),
      productIds: z.preprocess(
        (value) => {
          if (typeof value === "string") {
            return value.split(",").map(Number);
          }
          return value;
        },
        z.array(z.preprocess(Number, z.number())),
      ),
    }),
    z.object({
      type: z.literal("search"),
      projectId: z.uuid(),
      pageSize: z.preprocess(Number, z.number()),
      nextPage: z.string().optional(),
      searchTerm: z.string(),
    }),
  ])
  .describe("ProductsQuery");

export const productsQueryV2Schema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("specificExternalIds"),
      projectId: z.uuid(),
      pageSize: z.preprocess(Number, z.number()),
      nextPage: z.string().optional(),
      externalIds: z.preprocess(
        (value) => {
          if (typeof value === "string") {
            return value.split(",").map(Number);
          }
          return value;
        },
        z.array(z.preprocess(Number, z.number())),
      ),
      writeThroughProductsSync: coerceStringToBooleanSchema.nullish(),
    }),
    z.object({
      type: z.literal("search"),
      projectId: z.uuid(),
      pageSize: z.preprocess(Number, z.number()),
      nextPage: z.string().optional(),
      searchTerm: z.string(),
      writeThroughProductsSync: coerceStringToBooleanSchema.nullish(),
    }),
  ])
  .describe("ProductsQueryV2");

export const updateOptionValueMediaSchema = z
  .object({
    optionValueId: z.uuid(),
    media: z.array(
      z.object({
        assetId: z.uuid(),
        sortOrder: z.number(),
      }),
    ),
  })
  .describe("UpdateOptionValueMedia");

export const productMediaItemSchema = z
  .object({
    assetId: z.uuid(),
    sortOrder: z.number(),
    url: z.string(),
    type: z.enum(["image", "video"]),
  })
  .describe("ProductMediaItem");

export const productCheckoutMethodSchema = z
  .enum(["external", "replo"])
  .describe("ProductCheckoutMethod");

export const productCreateOrUpdateFormSchema = z
  .object({
    name: z.string(),
    productType: z.string(),
    price: z.number().nullable(),
    compareAtPrice: z.number().nullish(),
    description: z.string().nullish(),
    checkoutMethod: productCheckoutMethodSchema,
    externalCheckoutUrl: z.string().nullish(),
    images: z.array(z.string()),
    productFiles: z
      .array(
        z.object({
          id: z.string(),
          url: z.string(),
          name: z.string(),
          sizeBytes: z.number(),
          fileExtension: z.string(),
          label: z.string().optional(),
        }),
      )
      .nullish(),
    productEmail: productEmailFormBaseSchema.nullish(),
    media: z.array(productMediaItemSchema),
  })
  .superRefine((data, ctx) => {
    if (!data.name || data.name.trim().length === 0) {
      ctx.addIssue({
        code: "custom",
        message: "Your product must have a name",
        path: ["name"],
      });
    }

    if (data.price == null || Number.isNaN(data.price)) {
      ctx.addIssue({
        code: "custom",
        message: "Price is required",
        path: ["price"],
      });
    }

    if (data.checkoutMethod === "external") {
      const url = data.externalCheckoutUrl ?? "";
      if (url.trim().length === 0) {
        ctx.addIssue({
          code: "custom",
          message:
            "External checkout link is required when using your own checkout page",
          path: ["externalCheckoutUrl"],
        });
      } else if (!isValidHttpUrl(url)) {
        ctx.addIssue({
          code: "custom",
          message: "External checkout link must be a valid URL",
          path: ["externalCheckoutUrl"],
        });
      }
    }
  })
  .describe("ProductCreateOrUpdateForm");

export const productFormSchema = productCreateOrUpdateFormSchema
  .safeExtend({
    uploadingFileIds: z.array(z.string()).optional(),
    hasFulfillmentEmail: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (
      data.hasFulfillmentEmail &&
      (!data.productEmail?.header ||
        data.productEmail.header.trim().length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Email header is required",
        path: ["productEmail", "header"],
      });
    }
  })
  .describe("ProductFormSchema");
